import { Form, Input, Select } from "antd";
import { TTelegramBotDialogueRow } from "@lib/types/TelegramChatBot";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import {
  _BotOperationsEnum,
  AuthBotOperationsEnum,
  NoAuthBotOperationsEnum,
  OptionType,
} from "@lib/constants/Enums";
import { UUIDv4 } from "uuid-v4-validator";
import { useIntl } from "react-intl";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { StyledOptionsContainer } from "../index.styled";
import RULES from "@lib/schemas/general";

const { Option } = Select;

type Props = {
  dialogue?: TTelegramBotDialogueRow;
  onDragEnd?: (result: DropResult) => void;
};

export default function CollapseItemOptions({ dialogue, onDragEnd }: Props) {
  const { messages } = useIntl();

  const Options = dialogue?.requireAuth
    ? AuthBotOperationsEnum
    : NoAuthBotOperationsEnum;

  return (
    <Form.Item label={messages["common.options"] as string}>
      <Form.List name="options">
        {(fields, { add, remove }) => (
          <>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="options">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <Draggable
                        key={key}
                        draggableId={`key=${key}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            key={key}
                            style={{
                              ...provided.draggableProps.style,
                            }}
                            {...provided.dragHandleProps}
                            {...provided.draggableProps}
                            ref={provided.innerRef}
                          >
                            <StyledOptionsContainer
                              isDragging={snapshot.isDragging}
                            >
                              <ActionButton
                                type="move"
                                noLabel
                                noBg
                                style={{ marginRight: "4px" }}
                              />
                              <Form.Item
                                {...restField}
                                name={[name, 0, "text"]}
                                rules={[RULES.required]}
                                noStyle
                              >
                                <Input
                                  placeholder={
                                    messages["common.text"] as string
                                  }
                                />
                              </Form.Item>
                              <Form.Item noStyle name={[name, 0, "optionType"]}>
                                <Select
                                  placeholder={
                                    messages["common.selectOne"] as string
                                  }
                                >
                                  {Object.keys(OptionType).map((key) => (
                                    <Option key={key} value={key}>
                                      {
                                        messages[
                                          `TELEGRAM.OPTION_TYPE.${OptionType[key].toUpperCase()}`
                                        ] as string
                                      }
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                              <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) =>
                                  prevValues?.options?.[name]?.[0]
                                    ?.optionType !==
                                  currentValues?.options?.[name]?.[0]
                                    ?.optionType
                                }
                              >
                                {({ getFieldValue }) => {
                                  const selectedOptionType = getFieldValue([
                                    "options",
                                    name,
                                    0,
                                    "optionType",
                                  ]);
                                  if (selectedOptionType) {
                                    if (
                                      selectedOptionType === OptionType.web_app
                                    ) {
                                      return (
                                        <Form.Item
                                          rules={[RULES.required]}
                                          noStyle
                                          name={[name, 0, "web_app", "url"]}
                                        >
                                          <Input
                                            placeholder={
                                              messages["common.value"] as string
                                            }
                                          />
                                        </Form.Item>
                                      );
                                    } else if (
                                      selectedOptionType ===
                                      OptionType.callback_data
                                    ) {
                                      const selectedCallbackData =
                                        getFieldValue([
                                          "options",
                                          name,
                                          0,
                                          "callback_data",
                                        ]);

                                      if (
                                        UUIDv4.validate(selectedCallbackData)
                                      ) {
                                        return (
                                          <Form.Item
                                            rules={[RULES.required]}
                                            noStyle
                                            name={[name, 0, "callback_data"]}
                                          >
                                            <Input
                                              placeholder={
                                                messages[
                                                  "common.value"
                                                ] as string
                                              }
                                              readOnly
                                            />
                                          </Form.Item>
                                        );
                                      }

                                      return (
                                        <Form.Item
                                          noStyle
                                          name={[name, 0, "callback_data"]}
                                        >
                                          <Select
                                            placeholder={
                                              messages[
                                                "common.selectOne"
                                              ] as string
                                            }
                                          >
                                            {Object.keys(Options).map((key) => (
                                              <Option key={key} value={key}>
                                                {
                                                  messages[
                                                    `TELEGRAM.BOT_OPERATIONS.${Options[
                                                      key
                                                    ].toUpperCase()}`
                                                  ] as string
                                                }
                                              </Option>
                                            ))}
                                          </Select>
                                        </Form.Item>
                                      );
                                    }

                                    return (
                                      <Form.Item
                                        rules={[RULES.required]}
                                        noStyle
                                        name={[
                                          name,
                                          0,
                                          `${selectedOptionType}`,
                                        ]}
                                      >
                                        <Input
                                          placeholder={
                                            messages["common.value"] as string
                                          }
                                        />
                                      </Form.Item>
                                    );
                                  }

                                  return null;
                                }}
                              </Form.Item>
                              <ActionButton
                                type="delete"
                                onClick={() => remove(name)}
                                noLabel
                                noBg
                                style={{ marginLeft: "4px" }}
                                itemName="common.option"
                              />
                            </StyledOptionsContainer>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            <ActionButton
              type="add"
              onClick={() => (dialogue?.parentId ? add("", 0) : add(""))}
              label={messages["common.add"] as string}
              block
            />
          </>
        )}
      </Form.List>
    </Form.Item>
  );
}
